// Public share page header colors: `primary` = header background, `accent` = title, icons and bottom stripe.
export const DEFAULT_SHARE_THEME = { primary: '#00247D', accent: '#FBBF24' };

export const SHARE_THEME_PRESETS = [
  { name: 'Classic', primary: '#00247D', accent: '#FBBF24' },
  { name: 'Forest', primary: '#14532D', accent: '#FACC15' },
  { name: 'Crimson', primary: '#7F1D1D', accent: '#FFFFFF' },
  { name: 'Midnight', primary: '#0A0A0A', accent: '#A3E635' },
  { name: 'Ocean', primary: '#134E4A', accent: '#FB923C' },
  { name: 'Royal', primary: '#1E3A8A', accent: '#F87171' },
  { name: 'Sky', primary: '#E0F2FE', accent: '#0C4A6E' },
];

export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};

// Darken toward black by `pct` (0-1).
const darken = (hex, pct) => `#${rgb(hex).map((c) => Math.round(c * (1 - pct)).toString(16).padStart(2, '0')).join('')}`;

export const isLightColor = (hex) => {
  const [r, g, b] = rgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b > 165;
};

export const withAlpha = (hex, a) => `rgba(${rgb(hex).join(', ')}, ${a})`;
const inkOn = (hex) => (isLightColor(hex) ? '#0F172A' : '#FFFFFF');

// A saved custom theme, or null for the default navy / amber look.
const customTheme = (theme) => {
  const ok = theme && HEX_RE.test(theme.primary || '') && HEX_RE.test(theme.accent || '');
  const isDefault = ok && theme.primary.toUpperCase() === DEFAULT_SHARE_THEME.primary && theme.accent.toUpperCase() === DEFAULT_SHARE_THEME.accent;
  return ok && !isDefault ? theme : null;
};

// Styles for the share page hero. Default keeps the original navy -> slate look.
export function shareHero(theme) {
  const t = customTheme(theme);
  const primary = t ? t.primary : DEFAULT_SHARE_THEME.primary;
  const accent = t ? t.accent : DEFAULT_SHARE_THEME.accent;
  const light = isLightColor(primary);
  const background = !t
    ? 'linear-gradient(to bottom, #00247D, #001A5C, #0F172A)'
    : light
      ? `linear-gradient(to bottom, ${primary}, ${darken(primary, 0.12)})`
      : `linear-gradient(to bottom, ${primary}, ${darken(primary, 0.3)}, ${darken(primary, 0.6)})`;
  return {
    light,
    accent,
    header: { background, borderBottomColor: t ? accent : '#F59E0B' },
    tableHead: { backgroundColor: primary, color: inkOn(primary) },
    // Selected tab is filled with the accent once a league picks its colours.
    activeTab: t ? { backgroundColor: accent, color: inkOn(accent) } : undefined,
    // Header-colour bands (score header, team bars) and accent buttons on public game pages.
    band: { backgroundColor: primary, color: inkOn(primary) },
    accentFill: t ? { backgroundColor: accent, color: inkOn(accent) } : { backgroundColor: '#FBBF24', color: '#00247D' },
    text: light ? 'text-slate-700' : 'text-white/70',
    pill: light ? 'bg-black/5 border-black/10 text-slate-700' : 'bg-white/10 border-white/20 text-white/80',
  };
}

// Colours for the 1080x1080 Share Recap graphic: header colour background, accent highlights.
export function recapPalette(theme) {
  const t = customTheme(theme);
  if (!t) {
    return {
      light: false, base: '#0A0F2C', ink: '#FFFFFF', accent: '#FCD34D',
      background: 'linear-gradient(to bottom right, #0A0F2C, #0F1A4A, #001A5C)',
      glow: 'radial-gradient(circle at 12% 88%, rgba(245,158,11,0.10), transparent 40%), radial-gradient(circle at 88% 8%, rgba(59,130,246,0.14), transparent 45%)',
      banner: { backgroundColor: '#FBBF24', color: '#00247D' },
    };
  }
  const light = isLightColor(t.primary);
  return {
    light,
    base: light ? t.primary : darken(t.primary, 0.45),
    ink: light ? '#0F172A' : '#FFFFFF',
    accent: t.accent,
    background: light
      ? `linear-gradient(to bottom right, ${t.primary}, ${darken(t.primary, 0.12)})`
      : `linear-gradient(to bottom right, ${darken(t.primary, 0.45)}, ${t.primary}, ${darken(t.primary, 0.3)})`,
    glow: `radial-gradient(circle at 12% 88%, ${withAlpha(t.accent, 0.12)}, transparent 40%), radial-gradient(circle at 88% 8%, ${withAlpha(t.accent, 0.08)}, transparent 45%)`,
    banner: { backgroundColor: t.accent, color: inkOn(t.accent) },
  };
}
